import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';

const cookies = [
  {
    name: 'session_id',
    type: 'Essential',
    purpose: 'Maintains your login session',
    duration: 'Session',
  },
  {
    name: 'csrf_token',
    type: 'Essential',
    purpose: 'Prevents cross-site request forgery attacks',
    duration: 'Session',
  },
  {
    name: 'preferences',
    type: 'Functional',
    purpose: 'Stores your dashboard preferences',
    duration: '1 year',
  },
  {
    name: '_ga',
    type: 'Analytics',
    purpose: 'Google Analytics - tracks page views',
    duration: '2 years',
  },
  {
    name: '_gid',
    type: 'Analytics',
    purpose: 'Google Analytics - distinguishes users',
    duration: '24 hours',
  },
  {
    name: '_fbp',
    type: 'Marketing',
    purpose: 'Facebook - advertising and retargeting',
    duration: '3 months',
  },
  {
    name: 'hubspot',
    type: 'Marketing',
    purpose: 'HubSpot - marketing automation tracking',
    duration: '1 year',
  },
];

const CookiePolicyPage: React.FC = () => {
  const [analyticsEnabled, setAnalyticsEnabled] = React.useState(true);
  const [marketingEnabled, setMarketingEnabled] = React.useState(false);
  const [contactOpen, setContactOpen] = React.useState(false);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      <Box sx={{ pt: 15, pb: 10 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Legal" color="secondary" sx={{ mb: 2 }} />
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Cookie Policy
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Last updated: February 14, 2026
            </Typography>
          </Box>

          <Card sx={{ p: 4, mb: 4 }}>
            <Typography variant="body1" paragraph>
              This Cookie Policy explains how CleoLead ("we", "our", or "us") uses cookies and similar
              technologies when you visit our website and use our services. This policy provides you with clear
              and comprehensive information about the cookies we use and the purposes for using them.
            </Typography>
          </Card>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            What Are Cookies?
          </Typography>
          <Typography variant="body1" paragraph>
            Cookies are small text files that are placed on your device when you visit a website. They are
            widely used to make websites work more efficiently and to provide information to website owners.
            Cookies can be "persistent" (stored until they expire or are deleted) or "session" (deleted when
            you close your browser).
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            Types of Cookies We Use
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            Essential Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies are necessary for the website to function properly. They enable core functionality
            such as security, network management, and account access. You cannot opt out of these cookies
            as the website cannot function properly without them.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            Functional Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies enable enhanced functionality and personalization, such as remembering your preferences
            and settings. They may be set by us or by third-party providers whose services we use.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            Analytics Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies help us understand how visitors interact with our website by collecting and reporting
            information anonymously. This helps us improve our website and services.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            Marketing Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies are used to track visitors across websites to display relevant advertisements.
            They are also used to limit the number of times you see an ad and to measure the effectiveness
            of advertising campaigns.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            Cookies We Use
          </Typography>
          <TableContainer component={Card} sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Cookie Name</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Purpose</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cookies.map((cookie, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{cookie.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={cookie.type}
                        size="small"
                        color={
                          cookie.type === 'Essential' ? 'primary' :
                          cookie.type === 'Functional' ? 'secondary' :
                          cookie.type === 'Analytics' ? 'info' : 'warning'
                        }
                      />
                    </TableCell>
                    <TableCell>{cookie.purpose}</TableCell>
                    <TableCell>{cookie.duration}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            Manage Your Cookie Preferences
          </Typography>
          <Card sx={{ p: 4, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Settings color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Cookie Settings
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={<Switch checked disabled />}
                label={
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Essential Cookies</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Required for the website to function. Cannot be disabled.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={<Switch checked disabled />}
                label={
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Functional Cookies</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable enhanced functionality and personalization.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={analyticsEnabled}
                    onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Analytics Cookies</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Help us understand how you use our website.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={marketingEnabled}
                    onChange={(e) => setMarketingEnabled(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Marketing Cookies</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Used for advertising and retargeting.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Button variant="contained">
              Save Preferences
            </Button>
          </Card>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            How to Control Cookies in Your Browser
          </Typography>
          <Typography variant="body1" paragraph>
            Most web browsers allow you to control cookies through their settings. Here's how to manage
            cookies in popular browsers:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
            <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
            <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
            <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies</li>
          </Typography>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            Please note that disabling cookies may affect the functionality of our website and services.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            Third-Party Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            Some cookies are placed by third-party services that appear on our pages. We do not control
            the dissemination of these cookies. Please check the relevant third-party websites for more
            information about their cookies:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>Google Analytics: <a href="https://policies.google.com/privacy" style={{ color: '#3B82F6' }}>Privacy Policy</a></li>
            <li>Facebook: <a href="https://www.facebook.com/privacy/policy" style={{ color: '#3B82F6' }}>Privacy Policy</a></li>
            <li>HubSpot: <a href="https://legal.hubspot.com/privacy-policy" style={{ color: '#3B82F6' }}>Privacy Policy</a></li>
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            Updates to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Cookie Policy from time to time. We will notify you of any changes by posting
            the new policy on this page and updating the "Last updated" date.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about our use of cookies, please contact us at:
          </Typography>
          <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="body1">
              <strong>Email:</strong> <span onClick={() => setContactOpen(true)} style={{ color: '#C75B39', cursor: 'pointer', textDecoration: 'underline' }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setContactOpen(true); }}>support@cleolead.com</span>
            </Typography>
          </Card>
        </Container>
      </Box>

      <Footer />
      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Cookie Policy Inquiry"
        subtitle="Have questions about our use of cookies? Send us a message."
        formType="support"
        defaultSubject="Cookie Policy Inquiry"
      />
    </Box>
  );
};

export default CookiePolicyPage;
